import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { createBatchedParamsArrayFromIds, influencer, influencerQueries } from '@round/api';
import { useAddCreatorToPoolMutation } from '@round/creatorbase-helpers/api-hooks';
import { getCreatorPoolAccounts } from './helpers';

const exploreListOptions = influencerQueries.creatorExplore.options.list;

// Client influencer ids are UUIDs, so a selection of any size would outgrow the URL as one request.
const FETCH_BATCH_SIZE = 25;

export type AddCreatorsResult = {
    failedCreators: influencer.CreatorSearchResult[];
};

/**
 * Adds creators to a pool, one request per platform account not already in it. Creators are independent
 * of each other, so one failing leaves the rest added; a creator's own accounts are not, and it only
 * counts as added once all of them land.
 *
 * Accounts added here fold into the membership a row reports, so the table reflects them before the rows
 * themselves are refetched.
 */
export function useAddCreatorsToPool(poolId: number | null | undefined) {
    const queryClient = useQueryClient();
    const { addAsync } = useAddCreatorToPoolMutation(poolId);
    const [addedAccountIds, setAddedAccountIds] = useState<Set<number>>(() => new Set());

    const getPoolAccounts = (creator: influencer.CreatorSearchResult) =>
        getCreatorPoolAccounts(creator, poolId, addedAccountIds);

    const addCreators = async (creators: influencer.CreatorSearchResult[]): Promise<AddCreatorsResult> => {
        if (!creators.length) {
            return { failedCreators: [] };
        }

        const results = await Promise.all(
            creators.map(async (creator) => {
                const { accountIdsToAdd } = getPoolAccounts(creator);
                const accountResults = await Promise.allSettled(
                    accountIdsToAdd.map((id) => addAsync({ client_influencer_account_id: id }))
                );

                // Accounts are independent requests, so keep the ids that landed even when a sibling
                // fails: a creator left partly in the pool should read that way, not as untouched.
                return {
                    addedIds: accountIdsToAdd.filter((id, index) => accountResults[index].status === 'fulfilled'),
                    hasFailed: accountResults.some((result) => result.status === 'rejected'),
                };
            })
        );

        const failedCreators: influencer.CreatorSearchResult[] = [];
        const addedIds: number[] = [];

        results.forEach(({ addedIds: creatorAddedIds, hasFailed }, index) => {
            addedIds.push(...creatorAddedIds);

            if (hasFailed) {
                failedCreators.push(creators[index]);
            }
        });

        setAddedAccountIds((previous) => new Set([...previous, ...addedIds]));

        return { failedCreators };
    };

    /**
     * Fetches the creators behind a set of selection ids, for a selection the loaded rows no longer cover.
     * A client influencer id belongs to one client, so the ids alone are enough to match on, and none of
     * the drawer's own filters are sent: any of them could hide a selected creator.
     *
     * Throws if any batch fails, since a partial set would silently add fewer creators than were selected.
     */
    const fetchCreatorsByIds = async (creatorIds: string[]): Promise<influencer.CreatorSearchResult[]> => {
        if (!creatorIds.length) {
            // An empty id filter matches every creator, so nothing to look up has to mean no request.
            return [];
        }

        // Each batch asks for a page the size of the batch, so none of them is left behind unpaged.
        const batchedParams = createBatchedParamsArrayFromIds(
            creatorIds,
            (batch) => ({ client_influencer_id: batch.join(',') }),
            FETCH_BATCH_SIZE
        );

        const responses = await Promise.all(
            batchedParams.map((params) => queryClient.ensureQueryData(exploreListOptions(params)))
        );

        return responses.flatMap((response) => response.results);
    };

    // Stable and a no-op when already empty: the drawer clears this from an effect, which would
    // otherwise re-run on every render and set a fresh Set each time.
    const resetAddedAccounts = useCallback(() => {
        setAddedAccountIds((previous) => (previous.size ? new Set() : previous));
    }, []);

    return { addCreators, fetchCreatorsByIds, getPoolAccounts, resetAddedAccounts };
}
